import React, { useEffect, useState } from 'react';
import { GoogleOAuthProvider, useGoogleLogin } from '@react-oauth/google';
import {
  Container,
  Row,
  Col,
  Card,
  ListGroup,
  Button,
  Badge,
  Alert,
  Modal,
  Form
} from 'react-bootstrap';
import {
  FaGoogle,
  FaMicrosoft,
  FaExchangeAlt,
  FaCalendarAlt,
  FaLink,
  FaUnlink,
  FaExclamationTriangle,
  FaWhatsapp
} from 'react-icons/fa';
import apiService from '../../services/ApiServices';

const GOOGLE_CLIENT_ID = '1069600965731-i4gq6oojr22640a80m1uic2jn128jrl7.apps.googleusercontent.com';

// Renders the actual Google-provided button and redirects to the backend auth URL on click
function GoogleConnectButton({ authUrl }) {
  const login = useGoogleLogin({
    flow: 'auth-code',
    ux_mode: 'redirect',
    redirect_uri: authUrl,
    onError: (err) => console.error('Google connect error:', err),
  });

  const handleClick = () => {
    if (authUrl) {
      window.location.href = authUrl;
    } else {
      login();
    }
  };

  return (
    <button
      onClick={handleClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        background: '#fff',
        border: '1px solid #dadce0',
        borderRadius: '4px',
        padding: '0 12px',
        height: '40px',
        cursor: 'pointer',
        fontFamily: "'Google Sans', Roboto, Arial, sans-serif",
        fontSize: '14px',
        fontWeight: '500',
        color: '#3c4043',
        letterSpacing: '0.25px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
        transition: 'box-shadow 0.2s',
        whiteSpace: 'nowrap',
        outline: 'none',
      }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.2)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.12)'}
    >
      {/* Official Google G logo */}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 48 48"
        width="20"
        height="20"
        style={{ marginRight: 10, flexShrink: 0 }}
      >
        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
        <path fill="none" d="M0 0h48v48H0z" />
      </svg>
      Sign in with Google
    </button>
  );
}

function Integration() {
  const [calendars, setCalendars] = useState([]);
  const [whatsappStatus, setWhatsappStatus] = useState(false);
  const [whatsappConnections, setWhatsappConnections] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');

  const fetchCalendarIntegrations = async () => {
    try {
      const response = await apiService.get('/api/calendar/calendarlist');
      console.log('Fetched calendar integrations:', response);
      setCalendars(response.calendarList || []);
    } catch (error) {
      console.error('Error fetching calendar integrations:', error);
    }
  };

  const handleDisconnect = async (calendar) => {
    try {
      await apiService.post(`/api/calendar/disconnect`, {
        id: calendar.id,
        type: calendar.type,
      });
      fetchCalendarIntegrations();
    } catch (error) {
      console.error('Error disconnecting calendar:', error);
    }
  };

  useEffect(() => {
    fetchCalendarIntegrations();
    fetchWhatsappIntegrations();
  }, []);

  const fetchWhatsappIntegrations = async () => {
    try {
      const response = await apiService.get('/api/whatsapp/status');
      setWhatsappStatus(response.isConnected);
      setWhatsappConnections(response.connections || []);
    } catch (error) {
      console.error('Error fetching whatsapp status:', error);
    }
  };

  const handleConnectWhatsapp = async () => {
    try {
      await apiService.post('/api/whatsapp/connect', { phoneNumber });
      setShowModal(false);
      setPhoneNumber('');
      fetchWhatsappIntegrations();
    } catch (error) {
      console.error('Error connecting whatsapp:', error);
      alert('Error connecting WhatsApp. Please try again.');
    }
  };

  const handleDisconnectWhatsapp = async (integrationId) => {
    try {
      await apiService.post('/api/whatsapp/disconnect', { id: integrationId });
      fetchWhatsappIntegrations();
    } catch (error) {
      console.error('Error disconnecting whatsapp:', error);
    }
  };

  const handleTestWhatsappMessage = async (phoneNumber) => {
    try {
      const resp = await apiService.post('/api/whatsapp/test-message', { phoneNumber });
      alert(resp.message || 'Test message sent successfully!');
    } catch (error) {
      console.error('Error sending test message:', error);
      alert('Error sending test message. Check your console and API credentials.');
    }
  };

  const renderIcon = (type) => {
    switch (type.toUpperCase()) {
      case 'GOOGLE':
        return <FaGoogle className="text-danger" />;
      case 'OUTLOOK':
        return <FaMicrosoft className="text-primary" />;
      case 'EXCHANGE':
        return <FaExchangeAlt className="text-success" />;
      default:
        return <FaCalendarAlt className="text-secondary" />;
    }
  };

  const getCalendarColor = (type) => {
    switch (type.toUpperCase()) {
      case 'GOOGLE':
        return 'danger';
      case 'OUTLOOK':
        return 'primary';
      case 'EXCHANGE':
        return 'success';
      default:
        return 'secondary';
    }
  };

  return (
    <Container fluid className="py-4">
      <Row className="justify-content-center">
        <Col xs={12} lg={10} xl={8}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="p-4">
              <div className="mb-4">
                <h4 className="fw-bold text-dark mb-2">Integrations</h4>
                <p className="text-muted mb-0">
                  Connect your favorite tools and messaging apps.
                </p>
              </div>

              <div className="mb-3 mt-4">
                <h5 className="fw-bold text-dark">Calendar Integrations</h5>
                <p className="text-muted small">
                  Set which calendars we use to check for busy times and prevent double bookings.
                </p>
              </div>

              {/* Calendar List */}
              <ListGroup variant="flush">
                {calendars.map((calendar) => (
                  <ListGroup.Item key={calendar.id} className="px-0 py-3 border-bottom">
                    <Row className="align-items-center">
                      {/* Calendar Icon and Name */}
                      <Col xs={12} md={4} className="mb-2 mb-md-0">
                        <div className="d-flex align-items-center">
                          <div
                            className={`bg-${getCalendarColor(calendar.type)}-subtle rounded-circle p-2 me-3`}
                            style={{ width: '48px', height: '48px' }}
                          >
                            <div className="d-flex justify-content-center align-items-center h-100 fs-5">
                              {renderIcon(calendar.type)}
                            </div>
                          </div>
                          <div>
                            <h6 className="fw-bold mb-1">{calendar.name}</h6>
                            <small className="text-muted">{calendar.description}</small>
                          </div>
                        </div>
                      </Col>

                      {/* Connect Button */}
                      <Col xs={12} md={3} className="mb-2 mb-md-0">
                        {calendar.type?.toUpperCase() === 'GOOGLE' ? (
                          <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
                            <GoogleConnectButton authUrl={calendar.googleAuthUrl || calendar.authUrl} />
                          </GoogleOAuthProvider>
                        ) : (
                          <Button
                            variant="primary"
                            size="sm"
                            href={calendar.googleAuthUrl || calendar.authUrl}
                            className="w-100 w-md-auto"
                          >
                            <FaLink className="me-1" />
                            Connect
                          </Button>
                        )}
                      </Col>

                      {/* Status Badge */}
                      <Col xs={12} md={5}>
                        <div className="text-md-end">
                          {calendar.connections?.length > 0 ? (
                            <Badge bg="success" className="me-2">
                              Connected
                            </Badge>
                          ) : (
                            <Badge bg="secondary" className="me-2">
                              Not Connected
                            </Badge>
                          )}
                        </div>
                      </Col>
                    </Row>

                    {/* Connections List */}
                    {calendar.connections?.length > 0 && (
                      <Row className="mt-3">
                        <Col xs={12}>
                          {calendar.connections.map((connection) => (
                            <Alert
                              key={connection.id}
                              variant={connection.isConnected ? "success" : "warning"}
                              className="py-2 mb-2"
                            >
                              <Row className="align-items-center">
                                <Col xs={12} md={8}>
                                  <div className="d-flex align-items-center">
                                    {connection.isConnected ? (
                                      <FaLink className="me-2 text-success" />
                                    ) : (
                                      <FaExclamationTriangle className="me-2 text-warning" />
                                    )}
                                    <span className="small">
                                      {connection.isConnected ? (
                                        <>Connected with <strong>{connection.email}</strong></>
                                      ) : (
                                        <>Error connecting <strong>{connection.email}</strong>, try reconnect.</>
                                      )}
                                    </span>
                                  </div>
                                </Col>
                                <Col xs={12} md={4} className="text-md-end mt-2 mt-md-0">
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() =>
                                      handleDisconnect({
                                        id: connection.id,
                                        type: calendar.type,
                                      })
                                    }
                                  >
                                    <FaUnlink className="me-1" />
                                    Disconnect
                                  </Button>
                                </Col>
                              </Row>
                            </Alert>
                          ))}
                        </Col>
                      </Row>
                    )}
                  </ListGroup.Item>
                ))}
              </ListGroup>

              {/* Empty State */}
              {calendars.length === 0 && (
                <div className="text-center py-4 mb-4 bg-light rounded">
                  <FaCalendarAlt size={32} className="text-muted mb-2" />
                  <p className="text-muted mb-0">No Calendar Integrations</p>
                  <small className="text-muted">Connect your calendars to get started.</small>
                </div>
              )}

              {/* Messaging Apps Section */}
              <div className="mt-5 mb-3">
                <h5 className="fw-bold text-dark">Messaging Apps</h5>
                <p className="text-muted small">Connect messaging apps for receiving meeting notifications and reminders.</p>
              </div>
              <ListGroup variant="flush">
                <ListGroup.Item className="px-0 py-3 border-bottom">
                  <Row className="align-items-center">
                    <Col xs={12} md={4} className="mb-2 mb-md-0">
                      <div className="d-flex align-items-center">
                        <div
                          className="bg-success-subtle rounded-circle p-2 me-3"
                          style={{ width: '48px', height: '48px' }}
                        >
                          <div className="d-flex justify-content-center align-items-center h-100 fs-5">
                            <FaWhatsapp className="text-success" />
                          </div>
                        </div>
                        <div>
                          <h6 className="fw-bold mb-1">WhatsApp</h6>
                          <small className="text-muted">Receive meeting reminders via WhatsApp.</small>
                        </div>
                      </div>
                    </Col>

                    <Col xs={12} md={3} className="mb-2 mb-md-0">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => setShowModal(true)}
                        className="w-100 w-md-auto"
                      >
                        <FaLink className="me-1" />
                        Connect
                      </Button>
                    </Col>

                    <Col xs={12} md={5}>
                      <div className="text-md-end">
                        {whatsappStatus ? (
                          <Badge bg="success" className="me-2">Connected</Badge>
                        ) : (
                          <Badge bg="secondary" className="me-2">Not Connected</Badge>
                        )}
                      </div>
                    </Col>
                  </Row>

                  {/* Connections List */}
                  {whatsappConnections?.length > 0 && (
                    <Row className="mt-3">
                      <Col xs={12}>
                        {whatsappConnections.map((connection) => (
                          <Alert
                            key={connection.id}
                            variant={connection.isConnected ? "success" : "warning"}
                            className="py-2 mb-2"
                          >
                            <Row className="align-items-center">
                              <Col xs={12} md={8}>
                                <div className="d-flex align-items-center">
                                  {connection.isConnected ? (
                                    <FaLink className="me-2 text-success" />
                                  ) : (
                                    <FaExclamationTriangle className="me-2 text-warning" />
                                  )}
                                  <span className="small">
                                    {connection.isConnected ? (
                                      <>Connected with <strong>{connection.phoneNumber}</strong></>
                                    ) : (
                                      <>Error connecting <strong>{connection.phoneNumber}</strong>, try reconnect.</>
                                    )}
                                  </span>
                                </div>
                              </Col>
                              <Col xs={12} md={5} className="text-md-end mt-2 mt-md-0">
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  className="me-2"
                                  onClick={() => handleTestWhatsappMessage(connection.phoneNumber)}
                                >
                                  Test Message
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleDisconnectWhatsapp(connection.id)}
                                >
                                  <FaUnlink className="me-1" />
                                  Disconnect
                                </Button>
                              </Col>
                            </Row>
                          </Alert>
                        ))}
                      </Col>
                    </Row>
                  )}
                </ListGroup.Item>
              </ListGroup>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* WhatsApp Connect Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-5">Connect WhatsApp</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>WhatsApp Phone Number</Form.Label>
            <Form.Control
              type="text"
              placeholder="+1234567890"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
            />
            <Form.Text className="text-muted">
              Include country code e.g. +91XXXXXXXXXX
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
          <Button variant="success" onClick={handleConnectWhatsapp} disabled={!phoneNumber.trim()}>
            Connect WhatsApp
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Custom Styles */}
      <style>{`
        .bg-primary-subtle { background-color: #e3f2fd !important; }
        .bg-success-subtle { background-color: #e8f5e8 !important; }
        .bg-danger-subtle { background-color: #ffebee !important; }
        .bg-secondary-subtle { background-color: #f5f5f5 !important; }
        
        .card {
          border-radius: 12px;
        }
        
        .list-group-item {
          border: none;
          border-bottom: 1px solid #dee2e6 !important;
        }
        
        .list-group-item:last-child {
          border-bottom: none !important;
        }
        
        @media (max-width: 768px) {
          .card-body {
            padding: 1.5rem !important;
          }
        }
      `}</style>
    </Container>
  );
}

export default Integration;